/* ============================================================
   Hydro Heal — Fluid Interface Layer (behavior)
   - Reveal-on-scroll (opt-in via [data-hh-reveal])
   - Count-up stats ([data-hh-count], optional data-hh-prefix/suffix)
   - Segmented controls with a sliding, interruptible thumb ([data-hh-seg])
   - Interruptible accordion (details[data-hh-acc])
   - Instant press feedback (.hh-press)
   All motion respects prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var docEl = document.documentElement;
  docEl.classList.add('hh-motion');

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else { fn(); }
  }

  /* ---- Instant press feedback: respond on pointer-down ---- */
  document.addEventListener('pointerdown', function (e) {
    var el = e.target.closest ? e.target.closest('.hh-press') : null;
    if (!el) return;
    el.classList.add('hh-pressed');
    var clear = function () {
      el.classList.remove('hh-pressed');
      window.removeEventListener('pointerup', clear);
      window.removeEventListener('pointercancel', clear);
    };
    window.addEventListener('pointerup', clear);
    window.addEventListener('pointercancel', clear);
  }, { passive: true });

  ready(function () {

    /* ---- Reveal on scroll ---- */
    var revealEls = [].slice.call(document.querySelectorAll('[data-hh-reveal]'));
    if (revealEls.length && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('hh-in');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      revealEls.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.classList.add('hh-in'); /* already on screen: never hide it */
        } else {
          io.observe(el);
        }
      });
    } else {
      revealEls.forEach(function (el) { el.classList.add('hh-in'); });
    }

    /* ---- Count-up stats ---- */
    var counters = [].slice.call(document.querySelectorAll('[data-hh-count]'));
    function renderCount(el, value) {
      var prefix = el.getAttribute('data-hh-prefix') || '';
      var suffix = el.getAttribute('data-hh-suffix') || '';
      var plain = el.hasAttribute('data-hh-plain');
      var text = plain ? String(value) : value.toLocaleString('en-US');
      el.textContent = prefix + text + suffix;
    }
    function targetOf(el) {
      return parseInt(String(el.getAttribute('data-hh-count')).replace(/[^0-9]/g, ''), 10) || 0;
    }
    function runCount(el) {
      var target = targetOf(el);
      if (reduced.matches || !('requestAnimationFrame' in window)) { renderCount(el, target); return; }
      var dur = 1200;
      var t0 = performance.now();
      function tick(now) {
        var t = Math.min(1, (now - t0) / dur);
        var p = 1 - Math.pow(1 - t, 3);
        renderCount(el, Math.round(target * p));
        if (t < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }
    if (counters.length && 'IntersectionObserver' in window) {
      var cio = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { runCount(entry.target); cio.unobserve(entry.target); }
        });
      }, { threshold: 0.4 });
      counters.forEach(function (el) { cio.observe(el); });
    } else {
      counters.forEach(function (el) { renderCount(el, targetOf(el)); });
    }

    /* ---- Segmented control: sliding thumb, animates from the
       current on-screen value so re-taps mid-flight stay smooth ---- */
    var segs = [].slice.call(document.querySelectorAll('[data-hh-seg]'));
    segs.forEach(function (seg) {
      var thumb = document.createElement('span');
      thumb.className = 'hh-seg-thumb';
      seg.insertBefore(thumb, seg.firstChild);
      var anim = null;

      function activeItem() {
        return seg.querySelector('.on') || seg.querySelector('[aria-selected=true]');
      }
      function place(animate) {
        var item = activeItem();
        if (!item) { thumb.style.opacity = '0'; return; }
        var x = item.offsetLeft, y = item.offsetTop, w = item.offsetWidth, h = item.offsetHeight;
        var to = 'translate(' + x + 'px,' + y + 'px)';
        if (!animate || reduced.matches || !thumb.animate) {
          if (anim) { anim.cancel(); anim = null; }
          thumb.style.transform = to;
          thumb.style.width = w + 'px';
          thumb.style.height = h + 'px';
          thumb.style.opacity = '1';
          return;
        }
        var cs = getComputedStyle(thumb);
        var from = cs.transform === 'none' ? to : cs.transform;
        var fromW = cs.width, fromH = cs.height;
        if (anim) anim.cancel(); /* start from presentation value, never target */
        thumb.style.opacity = '1';
        anim = thumb.animate(
          [{ transform: from, width: fromW, height: fromH },
           { transform: to, width: w + 'px', height: h + 'px' }],
          { duration: 380, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' }
        );
        anim.onfinish = function () {
          thumb.style.transform = to;
          thumb.style.width = w + 'px';
          thumb.style.height = h + 'px';
          if (anim) { anim.cancel(); anim = null; }
        };
      }
      place(false);
      seg.addEventListener('click', function () {
        requestAnimationFrame(function () { place(true); });
      });
      window.addEventListener('resize', function () { place(false); });
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { place(false); });
      }
    });

    /* ---- Accordion: interruptible open/close ---- */
    var accs = [].slice.call(document.querySelectorAll('details[data-hh-acc]'));
    accs.forEach(function (details) {
      var summary = details.querySelector('summary');
      if (!summary) return;
      var anim = null;
      var closing = false, opening = false;
      details.style.overflow = 'hidden';

      function finishTo(open) {
        details.style.height = '';
        details.open = open;
        anim = null; closing = false; opening = false;
      }
      function animateHeight(from, to, open) {
        if (anim) anim.cancel();
        anim = details.animate(
          { height: [from + 'px', to + 'px'] },
          { duration: 380, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
        anim.onfinish = function () { finishTo(open); };
        anim.oncancel = function () { if (open) { opening = false; } else { closing = false; } };
      }
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (reduced.matches || !details.animate) { details.open = !details.open; return; }
        if (closing || !details.open) {
          /* expand — grab it mid-close and reverse from current height */
          var startH = details.offsetHeight;
          closing = false; opening = true;
          details.open = true;
          requestAnimationFrame(function () {
            var endH = details.scrollHeight;
            details.style.height = startH + 'px';
            animateHeight(startH, endH, true);
          });
        } else if (opening || details.open) {
          /* collapse — from wherever it currently is */
          opening = false; closing = true;
          var curH = details.offsetHeight;
          var endH2 = summary.offsetHeight;
          animateHeight(curH, endH2, false);
        }
      });
    });
  });
})();